import { useEffect, useRef } from "react";

const vertexShader = `
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0, 1);
}
`;

const fragmentShader = `
precision highp float;
uniform float uTime;
uniform vec3 uColor;
uniform float uAmplitude;
varying vec2 vUv;
void main() {
  vec2 uv = (vUv * 2.0 - 1.0);
  float d = -uTime * 0.5;
  float a = 0.0;
  for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
  }
  vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
  gl_FragColor = vec4(col, 1.0);
}
`;

interface IridescenceProps {
  color?: [number, number, number];
  speed?: number;
  amplitude?: number;
  className?: string;
}

function createShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string,
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error("Shader compile error:", gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function createProgram(
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader,
): WebGLProgram | null {
  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error("Program link error:", gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }
  return program;
}

export function Iridescence({
  color = [0.3, 0.6, 1],
  speed = 0.1,
  amplitude = 0.1,
  className,
}: IridescenceProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const uniformsRef = useRef<{
    uTime: WebGLUniformLocation | null;
    uColor: WebGLUniformLocation | null;
    uAmplitude: WebGLUniformLocation | null;
  } | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);
  const initializedRef = useRef(false);
  const accumulatedTimeRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const currentSpeedRef = useRef(speed);

  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;

    initializedRef.current = true;

    // Clear any existing canvases
    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }

    // Create canvas
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 400;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.display = "block";
    containerRef.current.appendChild(canvas);

    const gl = canvas.getContext("webgl", { antialias: true, alpha: false });
    if (!gl) {
      console.error("WebGL not supported");
      return;
    }
    glRef.current = gl;

    // Create shaders
    const vs = createShader(gl, gl.VERTEX_SHADER, vertexShader);
    const fs = createShader(gl, gl.FRAGMENT_SHADER, fragmentShader);
    if (!vs || !fs) return;

    const program = createProgram(gl, vs, fs);
    if (!program) return;
    programRef.current = program;

    gl.useProgram(program);

    // Get uniform locations
    uniformsRef.current = {
      uTime: gl.getUniformLocation(program, "uTime"),
      uColor: gl.getUniformLocation(program, "uColor"),
      uAmplitude: gl.getUniformLocation(program, "uAmplitude"),
    };

    // Set initial uniforms
    gl.uniform3f(uniformsRef.current.uColor, ...color);
    gl.uniform1f(uniformsRef.current.uAmplitude, amplitude);

    // Create fullscreen triangle (more efficient than quad)
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    // Fullscreen triangle vertices
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    );

    const positionLocation = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    gl.viewport(0, 0, canvas.width, canvas.height);

    const animate = (t: number) => {
      if (!gl || !uniformsRef.current) return;

      // Calculate delta time
      if (lastFrameTimeRef.current === 0) {
        lastFrameTimeRef.current = t;
      }
      const deltaTime = (t - lastFrameTimeRef.current) * 0.001;
      lastFrameTimeRef.current = t;

      // Accumulate time with current speed
      accumulatedTimeRef.current += deltaTime * currentSpeedRef.current;
      gl.uniform1f(uniformsRef.current.uTime, accumulatedTimeRef.current);

      gl.drawArrays(gl.TRIANGLES, 0, 3);
      animationFrameIdRef.current = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      if (containerRef.current && canvas.parentNode === containerRef.current) {
        containerRef.current.removeChild(canvas);
      }
      if (gl) {
        gl.getExtension("WEBGL_lose_context")?.loseContext();
      }
      initializedRef.current = false;
      glRef.current = null;
      programRef.current = null;
      uniformsRef.current = null;
    };
  }, []);

  // Update uniforms when props change
  useEffect(() => {
    if (glRef.current && uniformsRef.current && programRef.current) {
      const gl = glRef.current;
      gl.useProgram(programRef.current);
      gl.uniform1f(uniformsRef.current.uAmplitude, amplitude);
      currentSpeedRef.current = speed;
      gl.uniform3f(uniformsRef.current.uColor, ...color);
    }
  }, [amplitude, speed, color]);

  return (
    <div
      ref={containerRef}
      className={`w-full h-full relative ${className || ""}`}
    />
  );
}
